import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { Corners, Point } from '@/tools/image-engine';
import {
  arrowMove,
  canvasScale,
  drawCorners,
  drawLoupe,
  LOUPE_CSS_PX,
  nearestCorner,
  replaceCorner,
  toSourcePoint,
} from './corner-draw';

interface CornerEditorProps {
  readonly source: HTMLCanvasElement;
  readonly corners: Corners;
  /** Called while dragging (live) and on release (`final`). */
  readonly onChange: (corners: Corners, final: boolean) => void;
}

/** The two canvases: the photo with its corners, and the loupe; redrawn when the inputs change. */
function useCornerDrawing(
  source: HTMLCanvasElement,
  corners: Corners,
  selected: number,
  cornersRef: RefObject<Corners>,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const loupeRef = useRef<HTMLCanvasElement | null>(null);

  const draw = (): void => {
    if (canvasRef.current) drawCorners(canvasRef.current, source, cornersRef.current, selected);
  };
  const showLoupe = (corner: Point): void => {
    if (loupeRef.current) {
      drawLoupe(loupeRef.current, source, corner, canvasScale(canvasRef.current));
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = source.width;
    canvas.height = source.height;
    drawCorners(canvas, source, cornersRef.current, selected);
  }, [source, selected, cornersRef]);

  useEffect(() => {
    if (canvasRef.current) drawCorners(canvasRef.current, source, corners, selected);
  }, [corners, source, selected]);

  return { canvasRef, loupeRef, draw, showLoupe };
}

interface DragDeps {
  readonly source: HTMLCanvasElement;
  readonly cornersRef: RefObject<Corners>;
  readonly onChange: (corners: Corners, final: boolean) => void;
  readonly toSource: (x: number, y: number) => Point;
  readonly nearest: (p: Point) => number;
  readonly draw: () => void;
  readonly showLoupe: (corner: Point) => void;
  readonly setSelected: (index: number) => void;
}

/** Dragging a corner: the corner follows the pointer, a loupe shows the point under the finger. */
function useCornerDrag(d: DragDeps) {
  const { source, cornersRef, onChange, toSource, nearest, draw, showLoupe, setSelected } = d;
  const draggingRef = useRef(-1);
  const movedRef = useRef(false);
  const [loupe, setLoupe] = useState<{ side: 'left' | 'right' } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLCanvasElement>): void => {
    movedRef.current = false;
    const index = nearest(toSource(event.clientX, event.clientY));
    if (index < 0) return;
    draggingRef.current = index;
    setSelected(index);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const corner = cornersRef.current[index]!;
    setLoupe({ side: corner.x < source.width / 2 ? 'right' : 'left' });
    requestAnimationFrame(() => showLoupe(corner));
  };

  const onPointerMove = (event: PointerEvent<HTMLCanvasElement>): void => {
    const index = draggingRef.current;
    if (index < 0) return;
    movedRef.current = true;
    const point = toSource(event.clientX, event.clientY);
    const next = replaceCorner(cornersRef.current, index, point);
    cornersRef.current = next;
    onChange(next, false);
    draw();
    showLoupe(point);
  };

  const onPointerUp = (): void => {
    if (draggingRef.current < 0) return;
    draggingRef.current = -1;
    setLoupe(null);
    onChange(cornersRef.current, true);
  };

  return { loupe, movedRef, onPointerDown, onPointerMove, onPointerUp };
}

/** The editor's state and handlers: dragging a corner, clicking to place one, the keyboard. */
function useCornerEditor({ source, corners, onChange }: CornerEditorProps) {
  const cornersRef = useRef(corners);
  const [selected, setSelected] = useState(-1);

  cornersRef.current = corners;
  const { canvasRef, loupeRef, draw, showLoupe } = useCornerDrawing(
    source,
    corners,
    selected,
    cornersRef,
  );

  const toSource = (x: number, y: number): Point => toSourcePoint(canvasRef.current!, x, y);
  const nearest = (p: Point): number =>
    nearestCorner(cornersRef.current, p, canvasScale(canvasRef.current));
  const { loupe, movedRef, onPointerDown, onPointerMove, onPointerUp } = useCornerDrag({
    source,
    cornersRef,
    onChange,
    toSource,
    nearest,
    draw,
    showLoupe,
    setSelected,
  });

  const onClick = (event: MouseEvent<HTMLCanvasElement>): void => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    const point = toSource(event.clientX, event.clientY);
    const index = nearest(point);
    if (index >= 0) {
      setSelected(index);
      return;
    }
    if (selected >= 0) onChange(replaceCorner(cornersRef.current, selected, point), true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLCanvasElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelected((s) => (s < 0 ? 0 : (s + 1) % 4));
      return;
    }
    const index = selected < 0 ? 0 : selected;
    const moved = arrowMove(cornersRef.current[index]!, event.key, event.shiftKey, source);
    if (!moved) return;
    event.preventDefault();
    if (selected < 0) setSelected(0);
    onChange(replaceCorner(cornersRef.current, index, moved), true);
  };

  return {
    canvasRef,
    loupeRef,
    loupe,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onClick,
      onKeyDown,
    },
  };
}

/**
 * The photo with its four corners to place on the sheet's edges (#387 «Raddrizza» boards). Handles
 * keep a 44 px target whatever the photo's size; while a corner is dragged, a loupe magnifies the
 * point under the finger in the opposite top corner, so the finger never hides it. Operable
 * without dragging: click a corner then click where it goes (WCAG 2.5.7), or Enter to pick a
 * corner and the arrow keys to move it (WCAG 2.1.1).
 */
export function CornerEditor(props: CornerEditorProps) {
  const { t } = useTranslation('tools');
  const uid = useId();
  const { canvasRef, loupeRef, loupe, handlers } = useCornerEditor(props);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        role="application"
        tabIndex={0}
        aria-label={t('images.straighten.photoLabel')}
        aria-describedby={`${uid}-kbd`}
        {...handlers}
        style={{ touchAction: 'none' }}
        className="block h-auto w-full cursor-crosshair rounded-lg border border-border
          focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
          focus-visible:outline-none"
      />
      <span id={`${uid}-kbd`} className="sr-only">
        {t('images.straighten.keyboardHint')}
      </span>
      <canvas
        ref={loupeRef}
        aria-hidden="true"
        width={LOUPE_CSS_PX * 2}
        height={LOUPE_CSS_PX * 2}
        hidden={loupe === null}
        style={{ width: LOUPE_CSS_PX, height: LOUPE_CSS_PX }}
        className={`pointer-events-none absolute top-2 rounded-full border-[3px] border-card shadow-lg ${
          loupe?.side === 'left' ? 'left-2' : 'right-2'
        }`}
      />
    </div>
  );
}
